import { useState, useEffect, useCallback, useRef } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

interface UseSpeechRecognitionProps {
  onResult?: (text: string) => void;
  onPartialResult?: (text: string) => void;
  language?: string;
}

interface UseSpeechRecognitionReturn {
  isListening: boolean;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  toggleListening: () => void;
  clearTranscript: () => void;
  error: string | null;
}

export const useSpeechRecognition = ({
  onResult,
  onPartialResult,
  language = 'en-US'
}: UseSpeechRecognitionProps = {}): UseSpeechRecognitionReturn => {
  const [isListening, setIsListening] = useState(false);
  const [recognizer, setRecognizer] = useState<SpeechSDK.SpeechRecognizer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [sessionTranscript, setSessionTranscript] = useState('');
  const [currentPartialText, setCurrentPartialText] = useState('');
  const hasBeenStopped = useRef(false);
  const currentSessionTranscriptRef = useRef('');

  // Keep the ref in sync with the state
  useEffect(() => {
    currentSessionTranscriptRef.current = sessionTranscript;
  }, [sessionTranscript]);

  // Check if speech recognition is supported
  useEffect(() => {
    const speechKey = process.env.REACT_APP_SPEECH_KEY;
    const speechRegion = process.env.REACT_APP_SPEECH_REGION;
    
    setIsSupported(!!(speechKey && speechRegion && typeof window !== 'undefined'));
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      setError('Speech recognition is not supported or not configured');
      return;
    }

    try {
      setError(null);
      
      if (hasBeenStopped.current) {
        setSessionTranscript('');
        setCurrentPartialText('');
        // Clear the input field when starting fresh
        onPartialResult?.('');
        onResult?.('');
        // Reset the stopped flag
        hasBeenStopped.current = false;
      }
      
      if (hasBeenStopped.current) {
        hasBeenStopped.current = false;
      }
      
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        process.env.REACT_APP_SPEECH_KEY!,
        process.env.REACT_APP_SPEECH_REGION!
      );
      
      speechConfig.speechRecognitionLanguage = language;
      
      // Optimize for better recognition
      speechConfig.enableDictation();
      speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, "0");
      speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "0");
      
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const newRecognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      
      newRecognizer.recognizing = (s, e) => {
        if (onPartialResult && e.result.text) {
          const newText = e.result.text.trim();
          if (newText) {
            // Update the current partial text
            setCurrentPartialText(newText);
            // For partial results, show the session transcript plus current partial
            const completeTranscript = currentSessionTranscriptRef.current + (currentSessionTranscriptRef.current ? ' ' : '') + newText;
            onPartialResult(completeTranscript);
          }
        }
      };
      
      newRecognizer.recognized = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          if (onResult && e.result.text) {
            const newText = e.result.text.trim();
            if (newText) {
              // For final results, append to the session transcript and clear partial text
              setSessionTranscript(currentSessionTranscript => {
                const updatedSessionTranscript = currentSessionTranscript + (currentSessionTranscript ? ' ' : '') + newText;
                setCurrentPartialText(''); // Clear partial text since it's now final
                onResult(updatedSessionTranscript);
                return updatedSessionTranscript;
              });
            }
          }
          // Don't automatically stop - let user click to stop
        }
      };
      
      newRecognizer.canceled = (s, e) => {
        setIsListening(false);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          setError(`Speech recognition error: ${e.errorDetails}`);
        }
      };
      
      newRecognizer.sessionStopped = (s, e) => {
        setIsListening(false);
        newRecognizer.stopContinuousRecognitionAsync();
      };
      
      setRecognizer(newRecognizer);
      newRecognizer.startContinuousRecognitionAsync(
        () => {
          setIsListening(true);
        },
        (err) => {
          setError('Failed to start speech recognition');
          setIsListening(false);
        }
      );
    } catch (error) {
      setError('Failed to initialize speech recognition');
      setIsListening(false);
    }
  }, [isSupported, language, onResult, onPartialResult, currentPartialText]);

  const stopListening = useCallback(() => {
    if (recognizer) {
      recognizer.stopContinuousRecognitionAsync(
        () => {
          setIsListening(false);
          // Send the complete transcript (session + any remaining partial) when stopping
          const completeTranscript = currentSessionTranscriptRef.current + (currentSessionTranscriptRef.current && currentPartialText ? ' ' : '') + currentPartialText;
          if (completeTranscript && onResult) {
            onResult(completeTranscript);
          }
          // Mark that we've been stopped (for next fresh start)
          hasBeenStopped.current = true;
          // Reset the cleared flag when stopping
        },
        (err) => {
          setError('Failed to stop speech recognition');
          setIsListening(false);
          hasBeenStopped.current = true;
        }
      );
    }
  }, [recognizer, currentPartialText, onResult]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const clearTranscript = useCallback(() => {    
    setSessionTranscript('');
    setCurrentPartialText('');
    onPartialResult?.('');
    onResult?.('');
    
    currentSessionTranscriptRef.current = '';
  }, []);

  // Cleanup recognizer on unmount
  useEffect(() => {
    return () => {
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
      }
    };
  }, [recognizer]);

  return {
    isListening,
    isSupported,
    startListening,
    stopListening,
    toggleListening,
    clearTranscript,
    error
  };
}; 